import React, { useEffect, useRef, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import TestReportService, { MUUserItem } from "../../services/TestReport/TestReport.service";

type Props = { onSearch: (f: any) => void; onReset: () => void; totalRecords: number };

const simplifyOilName = (oilName?: string | null): string => {
  if (!oilName) return "—";
  const raw = oilName.trim();
  const up = raw.toUpperCase();
  const cleaned = raw.replace(/[()]/g, "").replace(/[,;]+/g, " ").trim();

  if (/\bOIL\b/i.test(cleaned)) {
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    if (tokens.length && tokens[tokens.length - 1].toUpperCase() === "OIL") tokens.pop();
    if (tokens.length) {
      const last = tokens[tokens.length - 1];
      if (/^[A-Za-z]+$/.test(last)) {
        return `${last.charAt(0).toUpperCase() + last.slice(1).toLowerCase()} Oil`;
      }
    }
    return cleaned;
  }

  {
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      const last = tokens[tokens.length - 1];
      if (/^[A-Za-z]+$/.test(last)) {
        return `${last.charAt(0).toUpperCase() + last.slice(1).toLowerCase()} Oil`;
      }
    }
  }

  return raw.length > 40 ? raw.slice(0, 37) + "..." : raw;
};

// Helper to alias MU names for display
const aliasManufacturingUnitName = (orig?: string | null, id?: number | null) => {
  if (id === 3) return "Unit-1-KR";  // K R Nagar
  if (id === 4) return "Unit-2-MT";  // Magadi Taluk
  const up = (orig ?? "").toUpperCase().replace(/\s+/g, " ").trim();
  if (/\bK\s*R\s*NAGAR\b/.test(up) || up.includes("KR NAGAR")) return "Unit-1-KR";
  if (up.includes("MAGADI TALUK") || up.includes("MAGADI")) return "Unit-2-MT";
  return orig ?? "";
};

const sanitizeBatchNo = (value: string) => value.replace(/[^0-9a-z]/gi, "").toUpperCase();

const TestReportSearch: React.FC<Props> = ({ onSearch, onReset }) => {
  const [oilTypes, setOilTypes] = useState<any[]>([]);
  const [byproductId, setByproductId] = useState<number | null>(null);
  const [manufacturingUnits, setManufacturingUnits] = useState<any[]>([]);
  const [posId, setPosId] = useState<number | null>(null);
  const [testers, setTesters] = useState<MUUserItem[]>([]);
  const [testerId, setTesterId] = useState<number | null>(null);
  const [batchNo, setBatchNo] = useState<string>("");
  const [testedStart, setTestedStart] = useState<Date | null>(null);
  const [keyword, setKeyword] = useState("");
  const toast = useRef<Toast>(null);

  useEffect(() => {
    (async () => {
      try {
        const [oilsRaw, mus] = await Promise.all([TestReportService.getOilTypes(), TestReportService.getManufacturingUnits()]);

        const oils = (oilsRaw ?? []).map((o: any) => ({
          id: o.id ?? o.value ?? o.Id,
          name: simplifyOilName(o.name ?? o.label ?? o.Name),
          originalName: o.name ?? o.label ?? o.Name,
        }));

        // Alias MU names for display and normalize id/name fields
        const musMapped = (mus ?? []).map((m: any) => {
          const id = Number(m?.id ?? m?.posID ?? m?.PosID);
          const originalName = m?.name ?? m?.posName ?? m?.PosName ?? m?.address2 ?? m?.Address2 ?? "";
          return {
            ...m,
            id, // ensure Dropdown optionValue="id" works
            name: aliasManufacturingUnitName(originalName, id), // show alias in dropdown
            originalName,
          };
        });

        setOilTypes(oils);
        setManufacturingUnits(musMapped);
      } catch (err) {
        console.error("Failed to load filters", err);
        setOilTypes([]);
        setManufacturingUnits([]);
      }
    })();
  }, []);

  useEffect(() => {
    setTesterId(null);
    setTesters([]);
    if (!posId) return;
    TestReportService.getOilTestersByManufacturingUnit(posId)
      .then((arr) => setTesters(arr ?? []))
      .catch(() => setTesters([]));
  }, [posId]);

  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const handleSearch = () => {
    if (!byproductId && !batchNo && !testedStart && !keyword.trim() && !posId && !testerId) {
      toast.current?.show({ severity: "warn", summary: "Pick at least one filter", life: 2500 });
      return;
    }

    const testedStartDate = testedStart ? fmt(testedStart) : undefined;
    const testedEndDate = testedStart ? fmt(testedStart) : undefined; // backend interprets end < next day

    onSearch({
      byproductId: byproductId ?? undefined,
      batchNo: batchNo || undefined,
      posId: posId ?? undefined,
      testerId: testerId ?? undefined,
      testedStartDate,
      testedEndDate,
      keyword: keyword.trim() || undefined,
    });
  };

  const handleReset = () => {
    setByproductId(null);
    setBatchNo("");
    setTestedStart(null);
    setKeyword("");
    setPosId(null);
    setTesterId(null);
    setTesters([]);
    onReset();
  };

  const muLabel = (m: any) => {
    const name = m?.name ?? m?.posName ?? m?.PosName;
    return name || "";
  };

  const shell = "w-full min-h-10 bg-white border border-green-500 rounded-md shadow-[0_0_0_2px_#22c55e] px-2 py-1";
  const controlClass = "w-full";

  const btnBorder = "border border-black rounded-md";

  return (
    <>
      <Toast ref={toast} />
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Oil Type */}
        <div className="md:col-span-2">
          <label className="block text-sm mb-1">Oils</label>
          <div className={shell}>
            <Dropdown
              value={byproductId}
              options={oilTypes}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => setByproductId(e.value)}
              className={controlClass}
              panelClassName="!rounded-md"
              showClear
              filter
              filterBy="name"
              filterPlaceholder="Search oils..."
              placeholder="Select oil"
              filterMatchMode="startsWith"
            />
          </div>
        </div>

        <div className="md:col-span-3">
          <label className="block text-sm mb-1">Manufacturing Unit</label>
          <div className={shell}>
            <Dropdown
              value={posId}
              options={manufacturingUnits}
              optionLabel="name"
              optionValue="id"
              itemTemplate={(o) => <span>{muLabel(o)}</span>}
              valueTemplate={(o, props) => <span>{o ? muLabel(o) : props.placeholder}</span>}
              onChange={(e) => setPosId(e.value)}
              className={controlClass}
              panelClassName="!rounded-md"
              placeholder="Select unit"
              showClear
            />
          </div>
        </div>

        <div className="md:col-span-3">
          <label className="block text-sm mb-1">Tested By</label>
          <div className={shell}>
            <Dropdown
              value={testerId}
              options={testers}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => setTesterId(e.value)}
              className={controlClass}
              panelClassName="!rounded-md"
              placeholder={!posId ? "(select unit first)" : testers.length ? "Select tester" : "No testers"}
              disabled={!posId || testers.length === 0}
              showClear
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm mb-1">Batch No</label>
          <div className={shell}>
            <InputText
              value={batchNo}
              onChange={(e) => setBatchNo(sanitizeBatchNo(e.target.value))}
              className={controlClass}
              placeholder="Enter batch no"
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm mb-1">Tested Date</label>
          <div className={shell}>
            <Calendar
              value={testedStart}
              onChange={(e) => setTestedStart(e.value as Date)}
              dateFormat="dd-mm-yy"
              className={controlClass}
              showIcon
              placeholder="Select date"
            />
          </div>
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <Button label="Filter" icon="pi pi-search" onClick={handleSearch} className={btnBorder} />
        <Button label="Reset" icon="pi pi-refresh" severity="secondary" onClick={handleReset} className={btnBorder} />
      </div>
    </>
  );
};

export default TestReportSearch;
